import videojs, { VideoJsPlayer } from 'video.js';

import { Event, EventData, EventName, LabelKey } from '@src/types/event';
import { getIsBrowser } from '@src/utils/device';

const Plugin = videojs.getPlugin('plugin');

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Options {}

class BaseNormalizer extends Plugin {
  vjsPlayer: VideoJsPlayer;

  constructor(player: VideoJsPlayer, options?: Options) {
    super(player, options);
    this.vjsPlayer = player;
  }

  trigger(eventName: EventName, data: EventData) {
    // skip trigger 'dispose' otherwise it will really dispose the tracker plugin
    if (eventName !== Event.DISPOSE) {
      this.vjsPlayer.eventManagerPlugin?.tracker?.trigger(eventName, data);
    }
    return super.trigger(eventName, data);
  }

  protected createCommonEventData() {
    const defaultLabelKeyValues = {
      [LabelKey.SESSION_ID]: this.getSessionId(),
      [LabelKey.VIDEO_ID]: this.getVideoId(),
      [LabelKey.VIDEO_URL]: this.getVideoObjUrl(),
      [LabelKey.TS]: new Date().getTime(),
      [LabelKey.VERSION]: PKG_VERSION,
      [LabelKey.URL]: getIsBrowser() ? window.location.href : undefined,
    };

    return {
      ...defaultLabelKeyValues,
      [LabelKey.IS_LIVE]: this.getIsLive(),
      [LabelKey.VIDEO_TIME]: this.getCurrentTime(),
    };
  }

  public getIsLive() {
    return this.vjsPlayer.duration() === Infinity;
  }

  public getDuration() {
    return this.getIsLive() ? 0 : this.vjsPlayer.duration();
  }

  public getCurrentTime() {
    // may be the player is already disposed
    if (!this.vjsPlayer.el()) return null;

    return this.getIsLive() ? 0 : this.vjsPlayer.currentTime();
  }

  public getMuted() {
    return this.vjsPlayer.muted();
  }

  public getAutoplay() {
    return this.vjsPlayer.autoplay();
  }

  public getSessionId() {
    return this.vjsPlayer.options_.sessionId;
  }

  public getVideoId() {
    return this.vjsPlayer.options_.videoId;
  }

  public getVideoObjUrl() {
    return this.vjsPlayer.options_.video?.url;
  }

  public getPlayerError() {
    return this.vjsPlayer.error();
  }

  public getIsIosFullscreen() {
    return videojs.browser.IS_IOS && this.vjsPlayer.isFullscreen();
  }

  dispose() {
    if (!this.player) return; // when player is already disposed
    super.dispose();
  }
}

export default BaseNormalizer;
